import {motion, useScroll, useSpring, useTransform} from 'framer-motion'
import {useRef} from 'react'

type ParallaxProps = {
  children: React.ReactNode
  speed?: number // +/- px factor mapped over scroll progress
  className?: string
  fadeIn?: boolean // fade in as it enters
  fadeOut?: boolean // fade out before it leaves
  enterEnd?: number // progress at which fade-in finishes (0..1)
  exitStart?: number // progress at which fade-out starts (0..1)
  maxOpacity?: number // peak opacity
}

export function ParallaxWrapper({
  children,
  speed = 0.25,
  className,
  fadeIn = true,
  fadeOut = true,
  enterEnd = 0.38,
  exitStart = 0.55,
  maxOpacity = 1,
}: ParallaxProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  const {scrollYProgress} = useScroll({
    target: ref,
    offset: ['start 90%', 'end 10%'],
  })

  const rawY = useTransform(scrollYProgress, [0, 1], [speed * 40, speed * -40])
  const y = useSpring(rawY, {stiffness: 80, damping: 20, mass: 0.3})

  const EPS = 1e-4
  const eEnd = Math.max(EPS, Math.min(1 - EPS, enterEnd))
  const exStart = Math.max(eEnd + EPS, Math.min(1, exitStart))

  const o0 = fadeIn ? 0 : maxOpacity
  const o1 = maxOpacity
  const o2 = maxOpacity
  const o3 = fadeOut ? 0 : maxOpacity

  const opacity = useTransform(scrollYProgress, [0, eEnd, exStart, 1], [o0, o1, o2, o3])

  return (
    <motion.div ref={ref} style={{y, opacity}} className={className}>
      {children}
    </motion.div>
  )
}
